import React, { useEffect, useMemo, useRef, useState } from "react";
import Input from "../../../shared/Input";
import Avatar from "../../../shared/Avatar";
import Button from "../../../shared/Button";
import { Modal } from "../../../shared";
import useForm from "../../../hooks/useForm";
import { useAuth } from "../../../hooks";
import { toast } from "react-toastify";
import { useAddMediaMutation } from "../../../redux/services/media/mediaApi";
import {
  useUpdateAccountMutation,
  useRequestEmailChangeMutation,
  useVerifyEmailChangeMutation,
  useCancelEmailChangeMutation,
  useResendEmailChangeMutation,
} from "../../../redux/services/account/accountApi";
import { ROOT_API_URL } from "../../../config/defaultSettings";
import { getParentCompany } from "../../../utils/authority";
import {
  pickProfileFields,
  isProfileDirty,
  isEmailChanged,
} from "../utils/profileFields";

const UserInfo = () => {
  const { user } = useAuth();
  const { form, updateForm, setFormInit } = useForm();
  const inputRef = useRef();
  const [avatar, setAvatar] = useState("");
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState("");
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [baselineProfile, setBaselineProfile] = useState(() =>
    pickProfileFields(null)
  );
  const [confirmEmailOpen, setConfirmEmailOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");

  const [addMedia] = useAddMediaMutation();
  const [updateAccount] = useUpdateAccountMutation();
  const [requestEmailChange] = useRequestEmailChangeMutation();
  const [verifyEmailChange] = useVerifyEmailChangeMutation();
  const [cancelEmailChange] = useCancelEmailChangeMutation();
  const [resendEmailChange] = useResendEmailChangeMutation();

  /** Re-sync form when server-backed profile fields change (login refresh, after save). */
  const profileSignature = useMemo(() => {
    if (!user) return "";
    return [
      user.email,
      user.pendingEmail,
      user.firstName,
      user.lastName,
      user.fullName,
      user.phone,
      user.updatedAt,
      user.avatar,
    ].join("\x1e");
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const slice = pickProfileFields(user);
    setBaselineProfile(slice);
    setFormInit(slice, { reset: true });
    setAvatar(user.avatar || "");
    // Reset local form only when the authenticated profile snapshot from the server changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally avoid depending on `user` identity to prevent wiping edits when context re-renders
  }, [profileSignature, user?._id, setFormInit]);

  useEffect(() => {
    // Clean up object URLs to avoid memory leaks.
    return () => {
      if (avatarPreviewUrl) {
        try {
          URL.revokeObjectURL(avatarPreviewUrl);
        } catch {
          // no-op
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onFormChange({ id, value }) {
    updateForm({ id, value });
  }

  const getApiOrigin = () => {
    // ROOT_API_URL includes `/api/v1`; uploaded files are served by the API host at the root.
    return String(ROOT_API_URL || "").replace(/\/api\/v1\/?$/, "");
  };

  const buildUploadedFileUrl = ({ uploadPath, filename }) => {
    const parentCompany = getParentCompany();
    const apiOrigin = getApiOrigin();
    if (!parentCompany || !apiOrigin || !uploadPath || !filename) return "";
    return `${apiOrigin}/${parentCompany}/${uploadPath}/${filename}`.replace(
      /([^:]\/)\/+/g,
      "$1"
    );
  };

  const currentProfile = useMemo(
    () => ({
      email: form.email ?? "",
      firstName: form.firstName ?? "",
      lastName: form.lastName ?? "",
      fullName: form.fullName ?? "",
      phone: form.phone ?? "",
    }),
    [
      form.email,
      form.firstName,
      form.lastName,
      form.fullName,
      form.phone,
    ]
  );

  const dirtyProfile = isProfileDirty(baselineProfile, currentProfile);
  const emailWillChange = isEmailChanged(baselineProfile, currentProfile);

  const nonEmailKeys = ["firstName", "lastName", "fullName", "phone"];
  const nonEmailDirty = nonEmailKeys.some(
    (key) =>
      (baselineProfile[key] ?? "") !== (currentProfile[key] ?? "")
  );

  async function persistNonEmailOnly() {
    if (!user?._id) return;
    try {
      setSavingProfile(true);
      const res = await updateAccount({
        firstName: currentProfile.firstName,
        lastName: currentProfile.lastName,
        fullName: currentProfile.fullName,
        phone: currentProfile.phone,
      }).unwrap();
      toast(res?.message || "Account updated", {
        toastId: "account-information",
        type: res?.status || "success",
      });
    } catch (err) {
      toast(err?.data?.message || err?.message || "Failed to update account", {
        toastId: "account-information-error",
        type: "error",
      });
    } finally {
      setSavingProfile(false);
    }
  }

  /**
   * Sends a verification code to the new email; optionally saves name/phone first.
   */
  async function submitEmailChangeRequest() {
    if (!user?._id) return;
    try {
      setSavingProfile(true);
      if (nonEmailDirty) {
        await updateAccount({
          firstName: currentProfile.firstName,
          lastName: currentProfile.lastName,
          fullName: currentProfile.fullName,
          phone: currentProfile.phone,
        }).unwrap();
      }
      const res = await requestEmailChange({
        email: currentProfile.email.trim(),
      }).unwrap();
      toast(res?.message || "Check your new inbox for a verification code.", {
        toastId: "account-email-change-sent",
        type: res?.status === "error" ? "error" : "success",
      });
      setConfirmEmailOpen(false);
      setVerificationCode("");
    } catch (err) {
      toast(
        err?.data?.message || err?.message || "Could not start email change",
        {
          toastId: "account-email-change-error",
          type: "error",
        }
      );
    } finally {
      setSavingProfile(false);
    }
  }

  async function handleVerifyEmailCode() {
    if (!verificationCode.trim()) {
      toast("Enter the verification code from your email.", {
        type: "warning",
        toastId: "verify-code-missing",
      });
      return;
    }
    try {
      setSavingProfile(true);
      const res = await verifyEmailChange({
        code: verificationCode.trim(),
      }).unwrap();
      toast(res?.message || "Email updated", {
        toastId: "verify-email-ok",
        type: res?.status || "success",
      });
      setVerificationCode("");
    } catch (err) {
      toast(err?.data?.message || err?.message || "Invalid or expired code", {
        toastId: "verify-email-fail",
        type: "error",
      });
    } finally {
      setSavingProfile(false);
    }
  }

  async function handleResendEmailCode() {
    try {
      setSavingProfile(true);
      const res = await resendEmailChange({}).unwrap();
      toast(res?.message || "A new code was sent.", {
        toastId: "resend-email-code",
        type: res?.status || "success",
      });
    } catch (err) {
      toast(err?.data?.message || err?.message || "Could not resend code", {
        toastId: "resend-email-fail",
        type: "error",
      });
    } finally {
      setSavingProfile(false);
    }
  }

  async function handleCancelPendingEmail() {
    try {
      setSavingProfile(true);
      const res = await cancelEmailChange().unwrap();
      toast(res?.message || "Cancelled", {
        toastId: "cancel-email-change",
        type: res?.status || "success",
      });
      setVerificationCode("");
    } catch (err) {
      toast(err?.data?.message || err?.message || "Could not cancel", {
        toastId: "cancel-email-fail",
        type: "error",
      });
    } finally {
      setSavingProfile(false);
    }
  }

  function handleSubmit() {
    if (!user?._id || !dirtyProfile) return;
    if (emailWillChange) {
      setConfirmEmailOpen(true);
      return;
    }
    persistNonEmailOnly();
  }

  function uploadAvatar() {
    inputRef.current.click();
  }

  async function handleFileChange(event) {
    const fileObj = event.target.files && event.target.files[0];
    if (!fileObj) {
      return;
    }

    // Reset input so selecting the same file again still fires change
    event.target.value = "";

    if (!fileObj.type?.startsWith("image/")) {
      toast("Please choose an image file (png/jpg/gif).", {
        type: "error",
        toastId: "avatar-invalid-type",
      });
      return;
    }

    // Optimistic preview
    try {
      if (avatarPreviewUrl) URL.revokeObjectURL(avatarPreviewUrl);
    } catch {
      // no-op
    }
    const preview = URL.createObjectURL(fileObj);
    setAvatarPreviewUrl(preview);
    setAvatar(preview);

    // Upload to media, then persist avatar URL on the user
    try {
      setUploadingAvatar(true);

      const uploadPath = `users/avatars/${user._id}`;
      const formData = new FormData();
      formData.append("refId", user._id);
      formData.append("refType", "user");
      formData.append("type", "avatar");
      formData.append("uploadPath", uploadPath);
      formData.append("docs", fileObj);

      const uploadRes = await addMedia({ formData }).unwrap();
      const uploaded = uploadRes?.data?.[0];

      // Prefer server-computed URL (uses API host + uploaded path virtual).
      // Fallback to local construction for safety/backwards compatibility.
      const avatarUrl =
        uploaded?.url ||
        buildUploadedFileUrl({ uploadPath, filename: uploaded?.filename });
      if (!avatarUrl) {
        throw new Error("Avatar upload succeeded but URL could not be built");
      }

      // Persist avatar URL via account update (self-service)
      const saveRes = await updateAccount({ avatar: avatarUrl }).unwrap();

      // Update local state with the persisted URL (not the blob preview)
      setAvatar(avatarUrl);
      toast(saveRes?.message || "Avatar updated", {
        type: saveRes?.status || "success",
        toastId: "avatar-updated",
      });
    } catch (err) {
      console.error("Avatar upload failed:", err);
      toast(err?.data?.message || err?.message || "Failed to upload avatar", {
        type: "error",
        toastId: "avatar-upload-failed",
      });
      // Restore original avatar on failure
      setAvatar(user?.avatar || "");
    } finally {
      setUploadingAvatar(false);
    }
  }

  if (!user) {
    return "Loading...";
  }

  return (
    <div>
      <Modal
        visible={confirmEmailOpen}
        setVisible={setConfirmEmailOpen}
        title="Confirm email change"
        size="sm"
        loading={savingProfile}
        confirm={submitEmailChangeRequest}
        submitText="Send verification code"
        cancelText="Cancel"
        content={
          <p className="text-sm text-gray-600">
            We will send a one-time code to{" "}
            <strong>{currentProfile.email.trim()}</strong>. Your sign-in email
            updates only after you enter that code. Continue?
          </p>
        }
      />
      <h3 className="text-2xl font-semibold text-gray-900">
        Account Information
      </h3>
      <div className="flex flex-col-reverse md:grid grid-cols-7 py-3">
        <div className="col-span-3">
          <div className="py-2">
            <Input
              id="email"
              onChange={onFormChange}
              value={form.email}
              size="sm"
              placeholder="Email"
              label="Email (sign-in)"
            />
            <div
              className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950"
              role="note"
            >
              Changing your email updates your login username for this
              workspace. Use an address you can access for password resets and
              verification.
            </div>
            {user.pendingEmail && user.email && (
              <p className="mt-2 text-xs text-gray-600">
                Current sign-in email until verified:{" "}
                <span className="font-medium">{user.email}</span>
              </p>
            )}
          </div>

          {user.pendingEmail ? (
            <div className="py-3 space-y-3 rounded-md border border-primary-200 bg-primary-50/50 p-4">
              <div className="text-sm font-semibold text-gray-800">
                Verify new email
              </div>
              <p className="text-xs text-gray-600">
                Enter the verification code sent to{" "}
                <span className="font-medium">{user.pendingEmail}</span>.
              </p>
              <Input
                id="emailVerificationCode"
                label="Verification code"
                placeholder="6-digit code"
                value={verificationCode}
                onChange={(target) =>
                  setVerificationCode(
                    typeof target === "string" ? target : target?.value ?? ""
                  )
                }
                size="sm"
              />
              <div className="flex flex-wrap gap-2 justify-end">
                <Button
                  type="alternative"
                  size="sm"
                  disabled={savingProfile}
                  onClick={handleCancelPendingEmail}
                >
                  Cancel change
                </Button>
                <Button
                  type="alternative"
                  size="sm"
                  disabled={savingProfile}
                  onClick={handleResendEmailCode}
                >
                  Resend code
                </Button>
                <Button
                  size="sm"
                  disabled={savingProfile}
                  onClick={handleVerifyEmailCode}
                >
                  Verify email
                </Button>
              </div>
            </div>
          ) : null}
          <div className="grid grid-cols-2 gap-x-3.5 py-2">
            <div className="flex flex-col">
              <Input
                id="firstName"
                onChange={onFormChange}
                value={form.firstName}
                size="sm"
                placeholder="First Name"
                label="First Name"
              />
            </div>
            <div className="flex flex-col">
              <Input
                id="lastName"
                onChange={onFormChange}
                value={form.lastName}
                size="sm"
                placeholder="Last Name"
                label="Last Name"
              />
            </div>
          </div>
          <div className="py-2">
            <Input
              id="fullName"
              onChange={onFormChange}
              value={form.fullName}
              size="sm"
              placeholder="Full Name"
              label="Full Name"
            />
          </div>
          <div className="py-2">
            <Input
              id="phone"
              onChange={onFormChange}
              value={form.phone}
              size="sm"
              placeholder="Phone"
              label="Phone"
            />
          </div>
          <div className="flex justify-end">
            <Button
              disabled={!dirtyProfile || savingProfile}
              onClick={handleSubmit}
            >
              {savingProfile ? "Saving..." : "Save changes"}
            </Button>
          </div>
        </div>
        <div className="col-start-4 col-end-6 px-12">
          <div className="flex flex-col items-center">
            <span className="text-xl mb-2">Avatar</span>
            <Avatar
              // Only show the spinner while we are actively uploading.
              // If the user has no avatar URL, Avatar should fall back to initials (not spin forever).
              loading={uploadingAvatar}
              src={avatar || user?.avatar || ""}
              name={user?.fullName}
              size="lg"
            />
            <input
              style={{ display: "none" }}
              ref={inputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
            />
            <Button
              className="mt-3"
              onClick={uploadAvatar}
              disabled={uploadingAvatar}
            >
              {uploadingAvatar ? "Uploading..." : "Upload Image"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserInfo;
